<template>
  <teleport to="body">
    <div class="debug-modal-backdrop" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header" :class="isClosed ? 'green-header' : 'red-header'">
          <h3 class="modal-title">{{ $t('gestionar_estado_ticket') }}</h3>
          <button type="button" class="btn-close" @click="closeModal">✕</button>
        </div>
        <div class="modal-body">
          <p>
            {{ isClosed ? $t('deseas_reabrir_ticket') : $t('deseas_cerrar_ticket') }}
            <strong>
              ticket nº {{ ticketId }}<span v-if="displayCreator"> de {{ displayCreator }}</span>
            </strong>
          </p>
      <!-- Select de motivos: carga desde motivosList y guarda en selectedMotivoId -->
      <div style="margin:10px 0; display:flex; gap:8px; align-items:center;">
        <label style="margin:0; font-weight:600;">{{ $t('seleccionar_motivo') }}</label>
        <select v-model="selectedMotivoId" style="padding:6px; border-radius:4px;">
          <option :value="null">— {{ $t('seleccionar') }} —</option>
          <option v-for="m in filteredMotivos" :key="m.id" :value="m.id">{{ m.descripcion }}</option>
        </select>
      </div>
      <label for="descripcion" style="margin-top: 10px; display: block;">
        {{ $t('comentario') }}
      </label>
      <textarea
        id="descripcion"
        v-model="descripcion"
        rows="3"
        style="width: 100%; margin-bottom: 10px;"
        :placeholder="$t('escribe_motivo')"
        :maxlength="maxMotivoLength"
      ></textarea>
      <div class="char-counter" aria-live="polite">
        <small :class="{'warning': remainingMotivoChars() <= 50}">
          {{ remainingMotivoChars() }} {{ $t('caracteres_restantes') || 'caracteres restantes' }}
        </small>
      </div>
      <div class="form-actions">
        <button
          type="button"
          :class="isClosed ? 'btn btn-danger' : 'btn btn-primary'"
          @click="handleAction"
          :disabled="(selectedMotivoId === null) || loading"
        >
          <span v-if="loading">{{ $t('actualizando') || 'Actualizando...' }}</span>
          <span v-else>{{ isClosed ? $t('reabrir_ticket') : $t('cerrar_ticket') }}</span>
        </button>
        <button type="button" class="btn btn-secondary" @click="closeModal">
          {{ $t('cancelar') }}
        </button>
      </div>
      </div>
    </div>
  </div>
  </teleport>
</template>

<script>
import api from '../api';
import { showAlert } from '@/utils/alerts';

export default {
  name: 'OpenCloseTicketModalView',
  emits: ['close', 'submit'],
  props: {
    ticketId: {
      type: Number,
      required: true,
    },
    isClosed: {
      type: Boolean,
      required: true,
    },
    // opcional: nombre completo del emisor/creador del ticket
    creadoPor: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      descripcion: '',
      motivosList: [],
      selectedMotivoId: null,
      maxMotivoLength: 1500,
      loading: false,
      creadoPorLocal: '',
    };
  },
  async mounted() {
    // Cargar motivos activos para el select (no bloqueante)
    this.loadMotivosList().catch(err => {
      if (import.meta.env.DEV) console.error('Error cargando motivos activos:', err);
    });

    // si no se proporciona el nombre del creador vía prop, intentar obtenerlo desde la API
    if (!this.creadoPor) {
      this.loadCreatorName().catch(err => {
        if (import.meta.env.DEV) console.debug('No se pudo cargar nombre del creador:', err);
      });
    }
  },
  methods: {
    closeModal() {
      this.$emit('close');
    },

    remainingMotivoChars() {
      // return a safe integer and avoid NaN when descripcion is null/undefined
      const len = this.descripcion ? String(this.descripcion).length : 0;
      return Math.max(0, this.maxMotivoLength - len);
    },
    
    async handleAction() {
      if (this.loading) return;
      // ensure descripcion doesn't exceed maxMotivoLength (defensive)
      if (this.descripcion && this.descripcion.length > this.maxMotivoLength) {
        this.descripcion = this.descripcion.slice(0, this.maxMotivoLength);
      }
      this.loading = true;
      
      try {
  if (import.meta.env.DEV) console.debug('Abriendo/cerrando ticket:', this.ticketId);
        
        // 1. Registrar motivo de cierre/reapertura
        const payload = {
          id_ticket: this.ticketId,
          descripcion: this.descripcion,
          esCierre: !this.isClosed,
          es: true,
          id_motivo: this.selectedMotivoId || null,
        };

        // debug: print payload and auth token presence
        console.debug('Posting /ticket-cierre payload:', payload);
        console.debug('Auth token present:', !!sessionStorage.getItem('auth_token'));

        await api.post('/ticket-cierre', payload);

        // 2. Cambiar estado del ticket
        const endpoint = this.isClosed
          ? `/tickets/${this.ticketId}/reopen`
          : `/tickets/${this.ticketId}/close`;

        const response = await api.put(endpoint, {});

  if (import.meta.env.DEV) console.debug('Respuesta del servidor:', response.data);

        // 3. Preparar datos para emitir
        const updatedTicket = {
          id: this.ticketId,
          estado: this.isClosed ? 'Abierto' : 'Cerrado',
          ...response.data
        };

        // 4. Emitir evento de éxito
        this.$emit('submit', updatedTicket);
        
        // 5. Mostrar alerta de éxito
        const title = this.isClosed 
          ? (this.$t('ticket_reabierto') || 'Ticket reabierto') 
          : (this.$t('ticket_cerrado') || 'Ticket cerrado');
        
        const message = this.isClosed
          ? (this.$t('swal_estado_actualizado_text', { id: this.ticketId }) || `El ticket nº ${this.ticketId} ha sido reabierto.`)
          : (this.$t('swal_estado_actualizado_text', { id: this.ticketId }) || `El ticket nº ${this.ticketId} ha sido cerrado.`);
        
        await showAlert('success', title, message);
        
      } catch (error) {
        // `api` interceptor normaliza el error en { status, data }
        // registrar todo el objeto para depuración
  if (import.meta.env.DEV) console.error('Error en el flujo de cierre/reapertura:', error);

        const errorTitle = this.$t('sweetalert_error_title') || 'Error';
  const errorMessage = this.$t('sweetalert_ticket_error', { id: this.ticketId }) || `Error al procesar el ticket nº ${this.ticketId}`;

        // Obtener mensaje desde la respuesta normalizada si existe
        const serverMsg = (error && error.data && (error.data.message || error.data.error)) || error.message || JSON.stringify(error);

        await showAlert('error', errorTitle, `${errorMessage}: ${serverMsg}`);
      } finally {
        this.loading = false;
      }
    },

    async loadMotivosList() {
      try {
        const res = await api.get('/tickets/motivos-cierre/activos');
        // el endpoint puede devolver { motivos: [...] } o un array directo
        this.motivosList = Array.isArray(res.data) ? res.data : (res.data.motivos || []);
      } catch (e) {
        // ignora errores no críticos
        this.motivosList = [];
        if (import.meta.env.DEV) console.error('Error loadMotivosList:', e);
      }
    },

    async loadCreatorName() {
      if (!this.ticketId) return;
      try {
        const res = await api.get(`/tickets/${this.ticketId}`);
        let data = res && res.data ? res.data : {};

        // algunos endpoints devuelven { data: { ... } }
        if (data && data.data) data = data.data;

        // búsqueda recursiva de campos con posible nombre completo
        const findName = (obj) => {
          if (!obj || typeof obj !== 'object') return null;

          // claves candidatas y combinaciones
          const keys = Object.keys(obj);

          // check directly on primitive fields
          const directChecks = [
            'full_name','nombre_completo','nombreCompleto','display_name','displayName','name','nombre','usuario_nombre','usuario_nombre_completo'
          ];
          for (const k of directChecks) {
            if (obj[k] && typeof obj[k] === 'string' && obj[k].trim()) return obj[k].trim();
          }

          // combos: nombre + apellidos or first_name + last_name
          if (obj.nombre && obj.apellidos) return `${String(obj.nombre).trim()} ${String(obj.apellidos).trim()}`;
          if (obj.first_name && obj.last_name) return `${String(obj.first_name).trim()} ${String(obj.last_name).trim()}`;

          // try some nested well-known props
          const candidateProps = ['user','creator','creador','owner','emisor','usuario'];
          for (const p of candidateProps) {
            if (obj[p] && typeof obj[p] === 'object') {
              const found = findName(obj[p]);
              if (found) return found;
            }
          }

          // deep scan nested objects
          for (const k of keys) {
            try {
              const val = obj[k];
              if (val && typeof val === 'object') {
                const nested = findName(val);
                if (nested) return nested;
              }
            } catch (e) {
              // ignore
            }
          }

          return null;
        };

        const nombre = findName(data) || '';
        this.creadoPorLocal = nombre || '';
      } catch (e) {
        if (import.meta.env.DEV) console.error('Error cargando ticket para obtener creador:', e);
        this.creadoPorLocal = '';
      }
    },
  },
  computed: {
    // mostrar solo motivos que coinciden con el tipo de acción (cierre vs reapertura)
    filteredMotivos() {
      const wantCierre = !this.isClosed; // si isClosed=false -> acción es cierre -> wantCierre=true
      return this.motivosList.filter(m => Boolean(m.esCierre) === Boolean(wantCierre));
    }
    ,
    // mostrar el nombre del creador: prop tiene prioridad, luego carga local
    displayCreator() {
      return this.creadoPor || this.creadoPorLocal || '';
    }
  },
  watch: {
    // al cambiar el modo (cerrar <-> reabrir) limpiar la selección
    isClosed() {
      this.selectedMotivoId = null;
    }
  }
};
</script>

<style scoped>
.modal-content-wrapper,
.modal-content {
  background-color: white;
  border-radius: 8px;
  width: 420px;           /* Ajusta el ancho del modal */
  max-width: 98vw;        /* Mejor adaptación en pantallas pequeñas */
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  box-sizing: border-box;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 18px;     /* Ajusta el padding para cabecera más compacta */
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  width: 100%;            /* Cabecera ocupa todo el ancho del modal */
  box-sizing: border-box;
  margin-top: 0;
  left: 0;
}

.modal-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: bold;
}

.btn-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: white;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0.8;
}

.btn-close:hover {
  opacity: 1;
}

.modal-body {
  padding: 18px;          /* Ajusta el padding para cuerpo más compacto */
  overflow-x: hidden;
  overflow-y: auto;
  max-width: 100%;        /* El cuerpo ocupa todo el ancho disponible */
  box-sizing: border-box;
  max-height: none;
}

label {
  font-weight: bold;
  color: #333;
  display: block;
  margin-bottom: 8px;
}

textarea {
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 10px;
  font-size: 1rem;
  resize: none;
  width: 100%;
  box-sizing: border-box;
  font-family: inherit;
}

textarea:focus {
  outline: none;
  border-color: #99172A;
  box-shadow: 0 0 0 2px rgba(153, 23, 42, 0.2);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
}

.btn {
  padding: 10px 24px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  transition: background-color 0.3s ease;
  min-width: 120px;
}

.btn-primary {
  background-color: #28a745;
  color: white;
}

.btn-primary:hover:not([disabled]) {
  background-color: #218838;
}

.btn-primary[disabled] {
  background-color: #6c757d !important;
  color: #ccc;
  cursor: not-allowed;
  opacity: 0.7;
}

.btn-danger {
  background-color: #99172A;
  color: white;
}

.btn-danger:hover:not([disabled]) {
  background-color: #7A1222;
}

.btn-danger[disabled] {
  background-color: #6c757d !important;
  color: #ccc;
  cursor: not-allowed;
  opacity: 0.7;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #5a6268;
}

.red-header {
  background-color: #99172A;
  color: white;
}

.green-header {
  background-color: #28a745;
  color: white;
}

@media (max-width: 900px) {
  .modal-content-wrapper,
  .modal-content {
    width: calc(100% - 32px) !important;
    max-width: calc(100% - 32px) !important;
  }
  
  .modal-title {
    font-size: 1.2rem;
  }
  
  .btn {
    padding: 8px 16px;
    min-width: 100px;
    font-size: 14px;
  }
}

/* Backdrop and stacking - keep it inside scoped to avoid global leakage but still apply to teleported content */
.debug-modal-backdrop {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.5) !important; /* match global overlay */
  z-index: 12000 !important;
}

.modal-content {
  z-index: 12001 !important;
}
</style>